import { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ExternalLink, Copy, Check, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { studio, setStudio } = useOutletContext();
  const { getAuthHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(`${window.location.origin}/s/${studio?.slug}`);
    setCopied(true);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePublishToggle = async () => {
    try {
      const response = await axios.put(`${API}/studios/me`, {
        is_published: !studio.is_published
      }, { headers: getAuthHeader() });
      setStudio(response.data);
      toast.success(response.data.is_published ? 'Studio published' : 'Studio unpublished');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="settings-page"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Settings</h1>
        <p className="text-white/60">Manage your studio settings</p>
      </div>

      <div className="max-w-2xl space-y-8">
        {/* Studio URL */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h2 className="font-semibold mb-4">Studio URL</h2>
          <div className="flex gap-2">
            <Input
              value={`${window.location.origin}/s/${studio?.slug || ''}`}
              readOnly
              className="bg-[#171717] border-white/10 flex-1"
            />
            <Button
              onClick={handleCopyUrl}
              variant="outline"
              className="border-white/20"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </Button>
            <a
              href={`/s/${studio?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-white/20">
                <ExternalLink size={18} />
              </Button>
            </a>
          </div>
        </div>

        {/* Custom Domain */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h2 className="font-semibold mb-4">Custom Domain</h2>
          <p className="text-white/60 text-sm mb-4">
            To use your own domain (e.g., wrap.yourstudio.com):
          </p>
          <ol className="text-white/50 text-sm space-y-2 list-decimal list-inside mb-4">
            <li>Add a CNAME record pointing to <code className="bg-[#171717] px-2 py-0.5 rounded">wrapviz.com</code></li>
            <li>Contact us at embermotors@gmail.com with your domain</li>
            <li>We'll configure SSL and routing for you</li>
          </ol>
          <p className="text-[#FFFFFF] text-xs">
            Custom domain setup is included in your plan
          </p>
        </div>

        {/* Publish Status */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold mb-1">Publish Status</h2>
              <p className="text-white/50 text-sm">
                {studio?.is_published 
                  ? 'Your studio is live and visible to customers' 
                  : 'Your studio is in draft mode'}
              </p>
            </div>
            <Button
              onClick={handlePublishToggle}
              disabled={studio?.subscription_status !== 'active'}
              variant={studio?.is_published ? 'outline' : 'default'}
              className={studio?.is_published ? 'border-white/20' : 'bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]'}
            >
              {studio?.is_published ? 'Unpublish' : 'Publish'}
            </Button>
          </div>
          {studio?.subscription_status !== 'active' && (
            <p className="text-yellow-400 text-xs mt-2">
              Subscribe to publish your studio
            </p>
          )}
        </div>

        {/* Danger Zone */}
        <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="text-red-400" size={20} />
            <h2 className="font-semibold text-red-400">Danger Zone</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <div>
                <p className="font-medium">Sign Out</p>
                <p className="text-white/40 text-sm">Sign out of your account</p>
              </div>
              <Button
                onClick={() => { logout(); navigate('/'); }}
                variant="outline"
                className="border-white/20"
              >
                Sign Out
              </Button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium">Delete Studio</p>
                <p className="text-white/40 text-sm">Permanently delete your studio and all data</p>
              </div>
              <Button
                onClick={() => toast.error('Contact support to delete your studio')}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsPage;
